import { FRAME_DURATION, snapToExportFrame } from '@/lib/timeline/exportFrameTiming';

export type FootageInsertionPlan =
  /** The playhead sits in kept footage: adding here would copy it. */
  | { kind: 'inside-scene'; hostIndex: number; canSplit: boolean }
  | { kind: 'add'; startTime: number; endTime: number }
  | { kind: 'none' };

const DEFAULT_FOOTAGE_SECONDS = 5;
const EPSILON = 1e-7;

/**
 * What "+" on the full-video timeline does at `time` (source seconds).
 * Inside a kept scene it must not silently append a copy of that footage —
 * the caller offers a split instead (`canSplit` uses the split rule: at least
 * one frame on each side). Elsewhere it adds up to 5 s of free footage, ending
 * where the next scene begins so the new scene never overlaps kept footage.
 */
export function planFootageInsertion({
  intervals,
  time,
  sourceLimit,
}: {
  intervals: ReadonlyArray<{ startTime: number; endTime: number }>;
  time: number;
  /** Last complete source frame time. */
  sourceLimit: number;
}): FootageInsertionPlan {
  const t = Math.max(0, Math.min(snapToExportFrame(time), sourceLimit));

  const hostIndex = intervals.findIndex(
    (it) => t >= snapToExportFrame(it.startTime) - EPSILON && t < snapToExportFrame(it.endTime) - EPSILON,
  );
  if (hostIndex >= 0) {
    const host = intervals[hostIndex];
    const canSplit =
      t >= snapToExportFrame(host.startTime) + FRAME_DURATION - EPSILON &&
      t <= snapToExportFrame(host.endTime) - FRAME_DURATION + EPSILON;
    return { kind: 'inside-scene', hostIndex, canSplit };
  }

  const nextSceneStart = intervals
    .map((it) => snapToExportFrame(it.startTime))
    .filter((start) => start > t + EPSILON)
    .reduce((earliest, start) => Math.min(earliest, start), Infinity);
  const endTime = snapToExportFrame(Math.min(t + DEFAULT_FOOTAGE_SECONDS, sourceLimit, nextSceneStart));
  if (endTime - t < FRAME_DURATION - EPSILON) return { kind: 'none' };
  return { kind: 'add', startTime: t, endTime };
}
